<template>
    <modal-ui
        :id="id"
        size="modal-lg"
        :title="isCreateMode ? 'Создание пользователя' : $t('users.editUser')">
        <form class="loader" @submit.prevent="submitForm">
            <loader-ui :is-show="state.loader" />
            <div class="row mb-4">
                <div class="col-12 col-md-6 mb-3">
                    <div class="form-group">
                        <label>{{ $t('modals.entrance.fields.lastName.label') }}</label>
                        <input-ui
                            v-model="state.lastName"
                            :placeholder="$t('modals.entrance.fields.lastName.placeholder')" />
                    </div>
                </div>
                <div class="col-12 col-md-6 mb-3">
                    <div class="form-group">
                        <label>{{ $t('modals.entrance.fields.firstName.label') }}</label>
                        <input-ui
                            v-model="state.firstName"
                            :placeholder="$t('modals.entrance.fields.firstName.placeholder')" />
                    </div>
                </div>
                <div class="col-12 col-md-6 mb-3">
                    <div class="form-group">
                        <label>{{ $t('modals.entrance.fields.middleName.label') }}</label>
                        <input-ui
                            v-model="state.middleName"
                            :placeholder="$t('modals.entrance.fields.middleName.placeholder')" />
                    </div>
                </div>
                <div class="col-12 col-md-6 mb-3">
                    <div class="form-group">
                        <label>{{ $t('modals.entrance.fields.iin.label') }}</label>
                        <input-ui
                            v-model="state.iin"
                            :placeholder="$t('modals.entrance.fields.iin.placeholder')" />
                    </div>
                </div>
                <div class="col-12 col-md-6 mb-3">
                    <div class="form-group">
                        <label>{{ $t('modals.entrance.fields.phone.label') }}</label>
                        <input-ui
                            v-model="state.phone"
                            :mask="'+7 (###) ### ## ##'"
                            placeholder="+7 (000) 000 00 00" />
                    </div>
                </div>
                <div class="col-12 col-md-6 mb-3">
                    <div class="form-group">
                        <label>{{ $t('users.login') }}</label>
                        <input-ui
                            v-model="state.login"
                            :placeholder="$t('users.login')" />
                    </div>
                </div>
                <div class="col-12 mb-3">
                    <div class="form-group password-field">
                        <label>{{ $t('users.password') }}</label>
                        <div class="password-input-wrapper">
                            <input-ui
                                v-model="state.password"
                                :type="state.showPassword ? 'text' : 'password'"
                                :placeholder="$t('users.password')" />
                            <button
                                class="btn btn-link btn-sm password-toggle"
                                type="button"
                                @click="state.showPassword = !state.showPassword">
                                <vue-feather
                                    size="16"
                                    :type="state.showPassword ? 'eye-off' : 'eye'" />
                            </button>
                        </div>
                        <small v-if="!isCreateMode" class="form-text text-muted">
                            {{ $t('users.passwordMinLength') }} (оставьте пустым, если не хотите менять пароль)
                        </small>
                    </div>
                </div>
                <div v-if="user.isSuperAdmin" class="col-12 mb-3">
                    <div class="form-group">
                        <label>Роль</label>
                        <select
                            v-model="state.roleAlias"
                            class="form-select">
                            <option
                                v-for="role in availableRoles"
                                :key="role.alias"
                                :value="role.alias">
                                {{ role.label }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-12 text-end">
                    <button-ui
                        button-class="btn-secondary me-2"
                        @click="cancel">
                        {{ $t('ui.cancel') }}
                    </button-ui>
                    <button-ui
                        button-class="btn-primary"
                        type="submit"
                        :disabled="state.loader">
                        {{ $t('ui.save') }}
                    </button-ui>
                </div>
            </div>
        </form>
    </modal-ui>
</template>

<script lang="ts">
import { defineComponent, reactive, watch, computed, ref, onMounted } from "vue";
import { useModal } from "@/composables/useModal";
import api from "@/api";
import useNotifications from "@/composables/useNotifications";
import { userStore } from "@/store/userStore";

export default defineComponent({
    name: "EditUserCredentialsModal",
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const modal = useModal(props.id);
        const state = reactive({
            loader: false,
            firstName: '',
            lastName: '',
            middleName: '',
            iin: '',
            phone: '',
            login: '',
            password: '',
            roleAlias: 'user',
            showPassword: false,
        });

        const { user } = userStore();
        const isCreateMode = computed(() => modal.data?.mode === 'create');

        const availableRoles = ref<{ alias: string; label: string }[]>([]);

        onMounted(async () => {
            try {
                const allowedAliases = new Set([
                    'admin',
                    'manager',
                    'user',
                    'ovpo_signatory',
                    'ovpo_operator',
                    'audit',
                    'finance_director',
                    'one_c_integration',
                ]);

                const response = await api.v1.info.roles.get();
                const roles = response.data.data || [];

                availableRoles.value = roles
                    .filter((r: any) => allowedAliases.has(r.alias))
                    .map((r: any) => ({
                        alias: r.alias,
                        label: r.name,
                    }));

                if (availableRoles.value.length && !availableRoles.value.find(r => r.alias === state.roleAlias)) {
                    state.roleAlias = availableRoles.value[0].alias;
                }
            } catch (e) {
                useNotifications.error('Ошибка', 'Не удалось загрузить список ролей');
            }
        });

        watch(
            () => modal.data,
            (data) => {
                if (data?.user) {
                    const u = data.user;
                    state.firstName = u.firstName || '';
                    state.lastName = u.lastName || '';
                    state.middleName = u.middleName || '';
                    state.iin = u.iin || '';
                    state.phone = u.phone || '';
                    state.login = u.login || '';
                    state.password = '';
                    state.showPassword = false;
                } else {
                    state.firstName = '';
                    state.lastName = '';
                    state.middleName = '';
                    state.iin = '';
                    state.phone = '';
                    state.login = '';
                    state.password = '';
                    state.showPassword = false;
                }
            },
            { immediate: true },
        );

        async function submitForm() {
            if (state.loader) return;

            if (isCreateMode.value) {
                if (!state.login.trim()) {
                    useNotifications.error('Ошибка', 'Логин обязателен');
                    return;
                }
                if (!state.password || state.password.length < 6) {
                    useNotifications.error('Ошибка', 'Пароль должен быть не менее 6 символов');
                    return;
                }
            } else {
                if (state.password && state.password.length < 6) {
                    useNotifications.error('Ошибка', 'Пароль должен быть не менее 6 символов');
                    return;
                }
            }

            state.loader = true;

            try {
                if (isCreateMode.value) {
                    await api.v1.users.create({
                        login: state.login.trim(),
                        password: state.password,
                        firstName: state.firstName ? state.firstName.trim() : undefined,
                        lastName: state.lastName ? state.lastName.trim() : undefined,
                        middleName: state.middleName ? state.middleName.trim() : undefined,
                        iin: state.iin ? state.iin.trim() : undefined,
                        phone: state.phone ? state.phone.trim() : undefined,
                        roleAlias: state.roleAlias,
                    });
                    useNotifications.success('Успех', 'Пользователь успешно создан');
                } else {
                    const updateData: any = {};

                    if (state.firstName !== undefined) updateData.firstName = state.firstName.trim();
                    if (state.lastName !== undefined) updateData.lastName = state.lastName.trim();
                    if (state.middleName !== undefined) updateData.middleName = state.middleName.trim();
                    if (state.iin !== undefined) updateData.iin = state.iin.trim();
                    if (state.phone !== undefined) updateData.phone = state.phone.trim();
                    if (state.login !== undefined) updateData.login = state.login.trim();
                    if (state.password && state.password.length > 0) updateData.password = state.password;

                    await api.v1.users.update(modal.data.user.id, updateData);
                    useNotifications.success('Успех', 'Данные пользователя успешно обновлены');
                }

                modal.ok();
            } catch (error: any) {
                const errorMessage =
                    error?.response?.data?.error?.msg ||
                    error?.response?.data?.error ||
                    'Не удалось выполнить операцию';
                useNotifications.error('Ошибка', errorMessage);
            } finally {
                state.loader = false;
            }
        }

        function cancel() {
            modal.cancel();
        }

        return {
            state,
            submitForm,
            cancel,
            isCreateMode,
            user,
            availableRoles,
        };
    },
});
</script>

<style scoped lang="scss">
.password-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.password-toggle {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    padding: 0;
    border: none;
}

.password-toggle :deep(svg) {
    stroke: #6c757d;
}
</style>

